import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  DocumentData,
  DocumentSnapshot,
  deleteDoc,
  doc,
  doc as firestoreDoc,
  getDoc
} from 'firebase/firestore';
import { Index, User, Voter } from '../../types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchParams, removeIndexesOf, searchIndex, splitKeywords } from '../../utils/indexer';

import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const highlightKeywords = (text: string, searchKeyword: string) => {
  if (!searchKeyword) return text;

  const keywords = splitKeywords(searchKeyword);
  const parts = text.split(new RegExp(`(${keywords.map(k => k.trim()).join('|')})`, 'gi'));

  return (
    <span>
      {parts.map((part, index) => 
        keywords.some(k => k.trim().toLowerCase() === part.toLowerCase()) ? 
          <span key={index} style={{
            color: 'black',
            padding: '0 2px',
            borderRadius: '2px',
            fontWeight: 'bold'
          }}>{part}</span> : 
          part
      )}
    </span>
  );
};

interface VoterDetailsProps {
  voterIndex: Index;
  searchKeyword: string;
  user: User | null;
  city_municipality?: string;
  province?: string;
  onEdit?: (voter: Voter) => void;
  onDelete?: (voter: Voter) => void;
  onViewDetails?: (voter: Voter) => void; 
  onSelect?: (voter: Voter) => void; 
};

const VoterDetails: React.FC<VoterDetailsProps> = ({
  voterIndex,
  searchKeyword,
  user,
  city_municipality,
  province,
  onEdit,
  onDelete,
  onViewDetails,
  onSelect
}) => {
  const [voter, setVoter] = useState<Voter | null>(null);

  const fetchVoterDetails = async (voterIndex: Index): Promise<Voter | null> => {
    try {
      // Extract the voter ID from the documentPath
      const voterId = voterIndex.documentPath.split('/').pop();
      if (!voterId) return null;

      const voterDocRef = firestoreDoc(db, 'voters', voterId);
      const voterDoc = await getDoc(voterDocRef);

      if (voterDoc.exists()) {
        return { id: voterDoc.id, ...voterDoc.data() } as Voter;
      }
      return null;
    } catch (error) {
      console.error('Error fetching voter details:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchVoter = async () => {
      const fetchedVoter = await fetchVoterDetails(voterIndex);
      if (fetchedVoter) {
        setVoter(fetchedVoter);
      }
    };
    if (voterIndex) {
      fetchVoter();
    }
  }, [voterIndex]);

  const handleDelete = async (voter: Voter) => {
    try {
      if ((onDelete) && (user)) {
        await deleteDoc(doc(db, 'voters', voter.id));
        await removeIndexesOf(voterIndex.document, voterIndex.field, voterIndex.documentPath);

        const latestPollResponsePath =
          `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
          + `provinces/${voter.province}/districts/${voter.district}/`
          + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
          + `precincts/${voter.precinct}/voters/${voter.name}`;
        const latestPollResponseDocRef = doc(db, latestPollResponsePath);
        const latestPollResponseDoc = await getDoc(latestPollResponseDocRef);
        if (latestPollResponseDoc.exists()) {
          await deleteDoc(latestPollResponseDocRef);
        }
        
        onDelete(voter);
      }
    } catch (error) {
      console.error('Error deleting voter:', error);
    }
  };

  const handleEdit = async (voter: Voter) => {
    if (onEdit) {
      onEdit(voter);
    }
  };

  const handleSelect = async (voter: Voter) => {
    if (onSelect) {
      onSelect(voter);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        width: '100%', 
        bgcolor: 'white', 
        border: '1px solid #e0e0e0', 
        borderRadius: 2, 
        p: 2 
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {highlightKeywords(voterIndex.value, searchKeyword)}
        </Typography>
        {voter && (
          <Typography variant="caption" gutterBottom>
            Precinct {voter.precinct}, {voter.barangay}, {voter.city_municipality}, {voter.province}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1}>
        {user?.permissions?.admin && onDelete && voter && user && (
          <Button 
            size="small" 
            variant="outlined" 
            color="error"
            onClick={() => handleDelete(voter)}
          >
            Delete
          </Button>
        )}
        {user?.permissions?.manage_voters && onEdit && voter && (
          <Button 
            size="small" 
            variant="outlined" 
            color="primary"
            onClick={() => handleEdit(voter)}
          >
            Edit
          </Button>
        )}
        {(user?.permissions?.view_voters || user?.permissions.validate_polls) && onViewDetails && voter && (
          <Button 
            size="small" 
            variant="outlined" 
            color="info"
            onClick={() => onViewDetails(voter)}
          >
            Details
          </Button>
        )}
        {onSelect && voter &&
          (!city_municipality || voter.city_municipality === city_municipality) &&
          (!province || voter.province === province) && (
          <Button 
            size="small" 
            variant="outlined" 
            color="success"
            onClick={() => handleSelect(voter)}
          >
            Select
          </Button>
        )}
      </Stack>
    </Box>
  );
};

interface VoterListControlProps {
  city_municipality?: string;
  province?: string;
  onEdit?: (voter: Voter) => void;
  onDelete?: (voter: Voter) => void;
  onViewDetails?: (voter: Voter) => void;
  onSelect?: (voter: Voter) => void;
}

const VoterListControl: React.FC<VoterListControlProps> = ({ city_municipality, province, onEdit, onDelete, onViewDetails, onSelect }) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [voterIndexes, setVoterIndexes] = useState<Index[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadNext, setLoadNext] = useState(false);
  const [lastVoterRef, setLastVoterRef] = useState<DocumentSnapshot<DocumentData, DocumentData> | null>(null);
  
  const PAGE_SIZE = 15;
  const [searchKeyword, setSearchKeyword] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  type VotersPage = {
    voterIndexes: Index[];
    hasMore: boolean;
    lastSnapshotDoc: DocumentSnapshot<DocumentData, DocumentData> | null;
  };

  const searchVoters = async (
    keyword: string,
    searchAfter: DocumentSnapshot<DocumentData, DocumentData> | null
  ): Promise<VotersPage> => {
    const keywords = splitKeywords(keyword);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: PAGE_SIZE,
      document: "voter",
      field: "name",
      searchAfter: searchAfter
    }
    const result = await searchIndex(searchParams);
    return {
      voterIndexes: result.indexes,
      hasMore: result.indexes.length >= searchParams.resultLimit,
      lastSnapshotDoc: result.lastSnapshotDoc
    };
  };

  const fetchVoters = async (
    keyword: string,
    searchAfter: DocumentSnapshot<DocumentData, DocumentData> | null
  ): Promise<VotersPage> => {
    try {
      return keyword ? await searchVoters(keyword, searchAfter) : await searchVoters('a', searchAfter);
    } catch (error) {
      console.error('Error fetching voters:', error);
    }
    return {
      voterIndexes: [],
      hasMore: false,
      lastSnapshotDoc: null
    };
  };

  useEffect(() => {
    setLoading(true);
    fetchVoters('', null).then((result) => {
      setVoterIndexes(result.voterIndexes); 
      setHasMore(result.hasMore);
      setLastVoterRef(result.lastSnapshotDoc);
      setLoading(false);
    });
  }, []);
  
  const focusSearchInput = useCallback(() => {
    // Delay to ensure any state updates have completed
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(
          searchKeyword.length, 
          searchKeyword.length
        );
      }
    }, 0);
  }, [searchKeyword]);

  useEffect(() => {
    // Prevent multiple simultaneous searches
    if (isSearching) return;

    const handler = setTimeout(() => {
      if (searchKeyword) {
        setLoading(true);
        setIsSearching(true);

        // If no more results and new search is a refinement of previous search
        const previousSearchKeyword = searchInputRef.current?.dataset.previousKeyword || '';
        const isRefinedSearch = !hasMore && searchKeyword.startsWith(previousSearchKeyword);

        const searchOperation = isRefinedSearch
          ? Promise.resolve({
              voterIndexes: voterIndexes.filter(voterIndex => {
                const keywords = splitKeywords(searchKeyword);
                return keywords.every(keyword => 
                  voterIndex.value.toLowerCase().includes(keyword.toLowerCase())
                );
              }),
              hasMore: false,
              lastSnapshotDoc: null
            })
          : fetchVoters(searchKeyword, null);

        searchOperation.then((result) => {
          setVoterIndexes(result.voterIndexes); 
          setHasMore(result.hasMore);
          setLastVoterRef(result.lastSnapshotDoc);
          setLoading(false);
          setIsSearching(false);
          
          // Focus the input and set cursor to end
          focusSearchInput();

          // Update the previous keyword data attribute
          if (searchInputRef.current) {
            searchInputRef.current.dataset.previousKeyword = searchKeyword;
          }
        }).catch(() => {
          setLoading(false);
          setIsSearching(false);
        });
      }
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKeyword]);

  useEffect(() => {
    if (loadNext) {
      setLoading(true);
      fetchVoters(searchKeyword, lastVoterRef).then((result) => {
        setVoterIndexes(prevIndexes => [...prevIndexes, ...result.voterIndexes]); 
        setHasMore(result.hasMore);
        setLastVoterRef(result.lastSnapshotDoc);
        setLoading(false);
      });
      setLoadNext(false);
    }
  }, [loadNext, searchKeyword]);
  
  const observer = useRef<IntersectionObserver | null>(null);
  
  const lastVoterElementRef = useCallback((node: HTMLLIElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setLoadNext(true);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleDelete = (voter: Voter) => {
    setVoterIndexes((prevVoters) => prevVoters.filter((v) => v.id !== voter.id));

    if (onDelete) {
      onDelete(voter);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          fullWidth
          label="Keyword"
          variant="outlined"
          inputRef={searchInputRef}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          disabled={isSearching}
        />
      </Box>
      {isSearching && <Box mb={2}><LinearProgress color="primary" /></Box>}
      <List>
        {voterIndexes.map((voterIndex, index) => (
          <ListItem 
            key={voterIndex.id} 
            ref={index === voterIndexes.length - 1 ? lastVoterElementRef : undefined}
            sx={{ 
              mb: 1, 
              p: 0, 
              width: '100%' 
            }}
          >
            <VoterDetails 
              voterIndex={voterIndex}
              searchKeyword={searchKeyword}
              user={user}
              city_municipality={city_municipality}
              province={province}
              onEdit={onEdit}
              onDelete={onDelete ? handleDelete : undefined}
              onViewDetails={onViewDetails}
              onSelect={onSelect}
            />
          </ListItem>
        ))}
      </List>
      {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      {!loading && voterIndexes.length === 0 && <Typography align="center">No Voters available</Typography>}
      {!loading && !hasMore && voterIndexes.length > 0 && <Typography align="center">No more voters to load. Total voters: {voterIndexes.length}</Typography>}
    </>
  );
};

export default VoterListControl;
